import isNumber from 'lodash/isNumber'
import { useSetState } from 'react-use'
import { useLocation, useHistory } from 'react-router-dom'
import React, { useState, useEffect, ChangeEvent, useContext } from 'react'

import * as loco from '@loco'
import Head from '../../../../components/Head'
import { Routes, TableTitle } from '@variables'
import { Container, Tabs, Tab } from '../../../../components/shared/styled'
import { ChaptersTableContext } from '../../../../context/ChaptersTable'
import NotificationTable from '../../../../components/Tables/Review/Notification'
import ChapterAndLectureTable from '../../../../components/Tables/Review/ChapterAndLecture'
import {
    CreationStatus,
    AdminChaptersQuery,
    useAdminChaptersQuery,
    OrderByDirectionInput,
    useCustomNotificationsQuery,
    BatchCustomNotificationsOrderByColumnInput
} from '@graphql'

type State = {
    title?: TableTitle
    itemsPerPage: number
    notifications: {
        page: number
        search: string
        orderBy: {
            direction: OrderByDirectionInput
            column: BatchCustomNotificationsOrderByColumnInput
        }
    }
}

const initValues = {
    page: 0,
    search: ''
}

type Lecture = AdminChaptersQuery['chapters'][0]['lectures'][0]
type ChapterOrLecture = AdminChaptersQuery['chapters'][0] & {
    parentId?: string
    tableData?: { isTreeExpanded?: boolean; id?: number }
}

const ReviewPage = () => {
    const history = useHistory()
    const location = useLocation()

    const { expandedIdxs, itemsPerPage, setChaptersTableState } = useContext(ChaptersTableContext)

    const [chaptersAndLectures, setChaptersAndLectures] = useState<ChapterOrLecture[]>([])

    const [state, setState] = useSetState<State>({
        title: undefined,
        itemsPerPage: 10,
        notifications: {
            ...initValues,
            orderBy: {
                direction: OrderByDirectionInput.asc,
                column: BatchCustomNotificationsOrderByColumnInput.title
            }
        }
    })

    const { title } = state
    const { search } = location

    const setTitle = (newTitle: TableTitle) => (e: ChangeEvent<{}>) => {
        setState({
            title: newTitle,
            notifications: {
                ...initValues,
                orderBy: {
                    column: state.notifications.orderBy.column,
                    direction: state.notifications.orderBy.direction
                }
            }
        })

        if (newTitle === TableTitle.CHAPTER_AND_LECTURE) {
            history.push({ search: Routes.CHAPTERS_SEARCH_PARAM })
        }
        if (newTitle === TableTitle.NOTIFICATION) {
            history.push({ search: Routes.NOTIFICATIONS_SEARCH_PARAM })
        }
    }

    useEffect(() => {
        if (search.includes(Routes.NOTIFICATIONS_SEARCH_PARAM)) {
            return setState({
                title: TableTitle.NOTIFICATION,
                notifications: {
                    ...initValues,
                    orderBy: {
                        column: state.notifications.orderBy.column,
                        direction: state.notifications.orderBy.direction
                    }
                }
            })
        }
        return setState({
            title: TableTitle.CHAPTER_AND_LECTURE,
            notifications: {
                ...initValues,
                orderBy: {
                    column: state.notifications.orderBy.column,
                    direction: state.notifications.orderBy.direction
                }
            }
        })
    }, [search])

    const { data, loading: chaptersLoading } = useAdminChaptersQuery({
        fetchPolicy: 'network-only',
        onError: (err) => console.log(err),
        skip: title !== TableTitle.CHAPTER_AND_LECTURE
    })

    const loading = chaptersLoading

    const { data: notificationData, loading: notificationLoading } = useCustomNotificationsQuery({
        fetchPolicy: 'cache-and-network',
        onError: (err) => console.log(err),
        skip: title !== TableTitle.NOTIFICATION,
        variables: {
            filter: {
                search: state.notifications.search,
                status: CreationStatus.PENDING
            },
            orderBy: {
                column: state.notifications.orderBy.column,
                direction: state.notifications.orderBy.direction
            },
            page: state.notifications.page,
            itemsPerPage: state.itemsPerPage
        }
    })

    const onChangePage = (page: number) => {
        setState({
            notifications: {
                page: page,
                search: state.notifications.search,
                orderBy: {
                    column: state.notifications.orderBy.column,
                    direction: state.notifications.orderBy.direction
                }
            }
        })
    }

    const onChangeItemsPerPage = (itemsPerPage: number) => {
        setState({ itemsPerPage: itemsPerPage })
    }

    const onSearchChange = (searchQuery: string) => {
        setState({
            notifications: {
                ...initValues,
                search: searchQuery,
                orderBy: {
                    column: state.notifications.orderBy.column,
                    direction: state.notifications.orderBy.direction
                }
            }
        })
    }

    const setTreeExpanded = (row: ChapterOrLecture, is: boolean) => {
        if (!isNumber(row?.tableData?.id)) return
        const newData = [...chaptersAndLectures]

        let currentChapter = chaptersAndLectures.find((i) => i.id === row.id)
        if (!currentChapter) return

        currentChapter = {
            ...currentChapter,
            tableData: {
                ...currentChapter?.tableData,
                isTreeExpanded: is
            }
        }

        const idx = row?.tableData?.id || 0
        newData[idx] = currentChapter

        if (is) {
            let newExpanded = []

            if (expandedIdxs.length > 0) {
                newExpanded = [...expandedIdxs, currentChapter.index]
            } else {
                newExpanded.push(currentChapter.index)
            }

            setChaptersTableState({ expandedIdxs: newExpanded })
        } else {
            const index = expandedIdxs.indexOf(currentChapter.index)
            const newExpanded = [...expandedIdxs]

            newExpanded.splice(index, 1)
            setChaptersTableState({ expandedIdxs: newExpanded })
        }

        setChaptersAndLectures(newData)
    }

    useEffect(() => {
        const combinedData = data?.chapters?.reduce((acc: any[], item: ChapterOrLecture) => {
            const currentChapter = chaptersAndLectures.find((i) => i.id === item.id)

            const isTreeExpanded =
                Boolean(currentChapter?.tableData?.isTreeExpanded) ||
                expandedIdxs.indexOf(item.index) !== -1

            return acc
                .concat({
                    ...item,
                    tableData: {
                        isTreeExpanded: isTreeExpanded
                    }
                })
                .concat(
                    ...item.lectures.map((lec: Lecture) => {
                        return {
                            ...lec,
                            parentId: item.id
                        }
                    })
                )
        }, [])

        const newData: ChapterOrLecture[] =
            combinedData?.filter(
                (i) =>
                    i.status === CreationStatus.PENDING ||
                    (i.status === CreationStatus.APPROVED &&
                        Boolean(
                            i.drafts.find(
                                (d: { isMerged: boolean; isRejected: boolean }) =>
                                    !d.isMerged && !d.isRejected
                            )
                        ))
            ) || []
        setChaptersAndLectures(newData)
    }, [data])

    // https://github.com/mbrn/material-table/issues/871#issuecomment-515026168
    const handleSortOrderChange = (
        orderedColumnId: number,
        orderDirection: OrderByDirectionInput
    ) => {
        if (orderedColumnId === 1 || orderedColumnId > 2) return

        let column = BatchCustomNotificationsOrderByColumnInput.title
        if (orderedColumnId === 2) column = BatchCustomNotificationsOrderByColumnInput.runAt

        setState({
            notifications: {
                ...state.notifications,
                orderBy: {
                    column: column,
                    direction: orderDirection
                }
            }
        })
    }

    return (
        <>
            <Head title={loco.seo.review.title} />

            <Container>
                <Tabs>
                    <Tab
                        type="button"
                        active={title === TableTitle.CHAPTER_AND_LECTURE}
                        onClick={setTitle(TableTitle.CHAPTER_AND_LECTURE)}
                    >
                        {TableTitle.CHAPTER_AND_LECTURE}
                    </Tab>
                    <Tab
                        type="button"
                        active={title === TableTitle.NOTIFICATION}
                        onClick={setTitle(TableTitle.NOTIFICATION)}
                    >
                        {loco.admin.list.notifications.label}
                    </Tab>
                </Tabs>

                {title === TableTitle.CHAPTER_AND_LECTURE && (
                    <ChapterAndLectureTable
                        isLoading={loading}
                        pageSize={itemsPerPage}
                        data={chaptersAndLectures}
                        setTreeExpanded={setTreeExpanded}
                        title={loco.admin.list.chapter.lecture.title}
                        onChangeItemsPerPage={(itemsPerPage) =>
                            setChaptersTableState({ itemsPerPage: itemsPerPage })
                        }
                    />
                )}

                {title === TableTitle.NOTIFICATION && (
                    <NotificationTable
                        onChangePage={onChangePage}
                        loading={notificationLoading}
                        pageSize={state.itemsPerPage}
                        title={TableTitle.NOTIFICATION}
                        page={state.notifications.page}
                        onSearchChange={onSearchChange}
                        onChangeItemsPerPage={onChangeItemsPerPage}
                        handleSortOrderChange={handleSortOrderChange}
                        data={notificationData?.customNotifications.data || []}
                        sortedColumnDirection={state.notifications.orderBy.direction}
                        totalCount={notificationData?.customNotifications.meta.items || 0}
                        sortedColumnId={
                            state.notifications.orderBy.column ===
                            BatchCustomNotificationsOrderByColumnInput.title
                                ? 0
                                : 2
                        }
                    />
                )}
            </Container>
        </>
    )
}

export default ReviewPage
